import { faPlus, faRocket, faSliders } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { DateTimeField } from '~/team_tools/components/DateTimeField'
import { getZone, showInZone } from '~/team_tools/components/timezone'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { TemplateEditor } from './TemplateEditor'
import { TemplateFormFields } from './TemplateForm'
import { runLabel } from './Templates'
import { mapLabel, maxPlayersFor, pickableMaps } from './maps'
import { blankForm, formFrom, parseFieldErrors, payloadOf, previewName, TemplateFormState } from './templateFormState'
import {
    Deployment,
    LootPool,
    MACHINE_TYPES,
    Options,
    Template,
    Window,
    deployTemplate,
    getJoinableRaces,
    JoinableRace,
    dismissDeployment,
    getDeployments,
    getOptions,
    getTemplates,
    lootDrawLabel,
    saveServerRules,
    ServerRules,
    saveTemplate,
    saveWindow,
} from './templatesApi'

/**
 * A server is a map, a spawn fee and a rank. Region is where you put it.
 *
 * That is the whole vocabulary an operator uses -- "a noob Cage at 25 cents
 * in EU" -- and the page is built from those words rather than from a list
 * of template rows. Pick the three, the template that matches lights up (or
 * an offer to create it), pick a region and a count, and go.
 *
 * Rank is not stored; it is read off the requirements a template already
 * carries. The thresholds are rules, kept on the server (one set for every
 * staff member, D-019) and editable from the rules popup:
 *
 *   public   no requirements checked
 *   noob     at most `noobMax` sessions
 *   pro      at least `proMin` sessions
 *
 * Loot is a rule too. The operator types nothing but a spawn fee; the
 * starting balance of each pool follows from the fee, and the reward range
 * follows from the balance. The constants are in the same popup, and a
 * template's pools can be overridden by hand in its form when a particular
 * server needs to be different.
 */
export type Rank = 'public' | 'noob' | 'pro' | 'custom'

/** Every number the page derives from another number. One place, so the
 *  question "where did 0.05 come from" has an answer. */
export type Rules = {
    noobMax: number
    proMin: number
    /** USDC pool starting balance = fee × this. */
    usdcPerFee: number
    /** USDC reward min/max = balance ÷ these. */
    usdcMinDiv: number
    usdcMaxDiv: number
    /** POINT pool starting balance = fee × this. */
    pointPerFee: number
    pointMinDiv: number
    pointMaxDiv: number
}

/** Fitted to the one template on record when this was written (USDC 3.00 →
 *  0.05..0.10, POINT 5000 → 10..100). Replace with the fleet's own numbers
 *  once more of them have been read. */
export const DEFAULT_RULES: Rules = {
    noobMax: 300,
    // 301, not 300: both lobby checks are inclusive, so a shared 300 let a
    // player with exactly 300 spawns into Noob AND Pro servers.
    proMin: 301,
    usdcPerFee: 12,
    usdcMinDiv: 60,
    usdcMaxDiv: 30,
    pointPerFee: 20000,
    pointMinDiv: 500,
    pointMaxDiv: 50,
}

/** Pro starts above where Noob stops. The lobby's limits are inclusive at
 *  both ends, so an equal pair lets one player into both. The server refuses
 *  an overlapping pair too. */
const withoutOverlap = (r: Rules): Rules => (r.proMin > r.noobMax ? r : { ...r, proMin: r.noobMax + 1 })

/** The page's Rules out of the shared row. These used to live in each
 *  browser's localStorage, so two people building the same kind got
 *  different loot pools (D-019); the server's row is now the only copy. */
const rulesFrom = (shared?: ServerRules): Rules => {
    if (!shared) return DEFAULT_RULES
    const picked = Object.fromEntries(
        (Object.keys(DEFAULT_RULES) as Array<keyof Rules>).map((k) => [k, Number(shared[k])])
    ) as Rules
    return withoutOverlap({ ...DEFAULT_RULES, ...picked })
}

export const RANK_COPY: Record<Rank, { label: string; style: string; on: string }> = {
    public: { label: 'Public', style: 'text-white/70', on: 'border-white/60 bg-white/10 text-white' },
    noob: { label: 'Noob', style: 'text-green-400', on: 'border-green-400/70 bg-green-400/10 text-green-400' },
    pro: { label: 'Pro', style: 'text-red-400', on: 'border-red-400/70 bg-red-400/10 text-red-400' },
    custom: { label: 'Custom', style: 'text-violet-300', on: 'border-violet-400/70 bg-violet-400/10 text-violet-300' },
}

const RANKS: Rank[] = ['public', 'noob', 'pro']

const rankHint = (r: Rank, rules: Rules) =>
    r === 'public'
        ? 'Anyone can join'
        : r === 'noob'
          ? `Up to ${rules.noobMax} spawns`
          : r === 'pro'
            ? `${rules.proMin}+ spawns`
            : 'Hand-set limits'

export const rankOf = (
    t: Pick<Template, 'check_requirements' | 'min_sessions' | 'max_sessions' | 'min_kills' | 'max_kills'>,
    rules: Rules
): Rank => {
    // The backend's rule (ServerProfile.rank), not an exact match on the
    // numbers in this browser. The server names servers "{rank}" and auto-adds
    // them to races by that rule, and an exact match here made a kind capped at
    // 250 read as "custom": picking Noob then said it did not exist and tried
    // to create a duplicate, which the unique name refuses. `rules` still
    // decides the limits a NEW kind is made with.
    void rules
    if (!t.check_requirements) return 'public'
    const noKills = !t.min_kills && !t.max_kills
    if (noKills && !t.min_sessions && t.max_sessions) return 'noob'
    if (noKills && t.min_sessions && !t.max_sessions) return 'pro'
    return 'custom'
}

const requirementsFor = (rank: Rank, given: Rules) => {
    const rules = withoutOverlap(given)
    if (rank === 'noob')
        return {
            check_requirements: true,
            min_sessions: '',
            max_sessions: String(rules.noobMax),
            min_kills: '',
            max_kills: '',
        }
    if (rank === 'pro')
        return {
            check_requirements: true,
            min_sessions: String(rules.proMin),
            max_sessions: '',
            min_kills: '',
            max_kills: '',
        }
    return { check_requirements: false, min_sessions: '', max_sessions: '', min_kills: '', max_kills: '' }
}

const fee = (v: string | null | number) => (v && Number(v) > 0 ? Number(v).toFixed(2) : '0.00')
const money = (v: string | null | number) => (Number(fee(v)) > 0 ? `$${fee(v)}` : 'free')
const r2 = (n: number) => (Math.round(n * 100) / 100).toFixed(2)

/** An enabled kind if there is one, else a disabled twin. Kind names are unique,
 *  so dropping the disabled one would make the board try to recreate it and fail;
 *  returning it lets the build refuse it instead. */
const preferEnabled = (list: Template[], pred: (t: Template) => boolean) =>
    list.find((t) => t.enabled && pred(t)) ?? list.find(pred)

/** The pools a fee earns under the rules. A free server gets none: there is
 *  no fee to fund a pool from, and a pool on a free server pays out of
 *  nothing. */
export const lootFor = (feeValue: string, rules: Rules, balances?: { usdc?: string; point?: string }): LootPool[] => {
    const f = Number(feeValue)
    const usdc = balances?.usdc !== undefined && balances.usdc !== '' ? Number(balances.usdc) : f * rules.usdcPerFee
    const point =
        balances?.point !== undefined && balances.point !== '' ? Number(balances.point) : f * rules.pointPerFee
    const pools: LootPool[] = []
    if (usdc > 0)
        pools.push({
            currency: 'USDC',
            amount: r2(usdc),
            reward_min: r2(usdc / rules.usdcMinDiv),
            reward_max: r2(usdc / rules.usdcMaxDiv),
            auto_add_from_spawn_fee: '0.00',
        })
    if (point > 0)
        pools.push({
            currency: 'POINT',
            amount: r2(point),
            reward_min: r2(point / rules.pointMinDiv),
            reward_max: r2(point / rules.pointMaxDiv),
            auto_add_from_spawn_fee: '0.00',
        })
    return pools
}

const samePools = (a: LootPool[], b: LootPool[]) =>
    a.length === b.length &&
    a.every((p) => {
        const q = b.find((x) => x.currency === p.currency)
        return (
            q &&
            r2(Number(q.amount)) === r2(Number(p.amount)) &&
            r2(Number(q.reward_min)) === r2(Number(p.reward_min)) &&
            r2(Number(q.reward_max)) === r2(Number(p.reward_max))
        )
    })

/** What an event is made of.
 *
 *  The main map takes 80% of the crowd. The other 20% goes to the other
 *  maps in a fixed order of preference -- Radio Station, then Cage, then
 *  Hangar (and Military Base, when it is not the main one) -- halving down
 *  the list, so the second map gets most of the remainder and the last a
 *  sliver. Fees per map are fixed: Military Base runs at three prices, the
 *  small maps at two. Weights are the share of a region x rank group's
 *  people, and sum to one.
 */
const MAIN_SHARE = 0.8
/** Most wanted first. Of the maps picked, the earliest here is the main one. */
const MAP_PREFERENCE = ['MilitaryBase_v3', 'AbandonedRadioStation', 'TheCage', 'TheHangar']
const MAP_FEES: Record<string, Array<{ fee: string; weight: number }>> = {
    MilitaryBase_v3: [
        { fee: '0.25', weight: 0.45 },
        { fee: '0.50', weight: 0.3 },
        { fee: '1.00', weight: 0.25 },
    ],
    AbandonedRadioStation: [
        { fee: '0.25', weight: 0.6 },
        { fee: '1.00', weight: 0.4 },
    ],
    TheCage: [
        { fee: '0.25', weight: 0.6 },
        { fee: '1.00', weight: 0.4 },
    ],
    TheHangar: [
        { fee: '0.25', weight: 0.6 },
        { fee: '1.00', weight: 0.4 },
    ],
}

/** The maps picked, in preference order, with their share of the crowd:
 *  the first takes MAIN_SHARE, the rest halve down the remainder. One map
 *  picked means it takes everything. */
const eventMix = (picked: string[]): Array<{ map: string; fee: string; weight: number }> => {
    const ordered = MAP_PREFERENCE.filter((m) => picked.includes(m) && MAP_FEES[m])
    if (ordered.length === 0) return []
    if (ordered.length === 1)
        return (MAP_FEES[ordered[0]] ?? []).map((f) => ({ map: ordered[0], fee: f.fee, weight: f.weight }))
    const [main, ...others] = ordered
    const raw = others.map((_, i) => 1 / 2 ** (i + 1))
    const rawSum = raw.reduce((a, b) => a + b, 0)
    const shares: Array<[string, number]> = [
        [main, MAIN_SHARE],
        ...others.map((m, i) => [m, ((1 - MAIN_SHARE) * raw[i]) / rawSum] as [string, number]),
    ]
    return shares.flatMap(([map, share]) =>
        (MAP_FEES[map] ?? []).map((f) => ({ map, fee: f.fee, weight: share * f.weight }))
    )
}

/** Zone, rank, map: "EU #1 - PUBLIC - Military Base". */
export const DEFAULT_NAME = '{zone} #{index} - {rank} - {map}'

const ZONE: Record<string, string> = { us: 'NA', sa: 'SA', eu: 'EU', sea: 'SEA' }

/** What a server is CALLED, mirroring `gameapi.regions.SLOT_REGION_NAME`.
 *
 *  Differs from ZONE in one entry: a US box is called "US" and matched as
 *  "NA". ZONE is the MATCHING vocabulary -- what a race's auto-add rule stores
 *  and what the region chips show -- and the two do not have to be the same
 *  word. Only this one appears in a name. */
const NAME_REGION: Record<string, string> = { us: 'US', sa: 'SA', eu: 'EU', sea: 'SEA' }

const CHIP_OFF = 'border-white/15 text-white/40 hover:border-white/30 hover:text-white/70'
const INPUT =
    'rounded border border-white/25 bg-transparent px-2 py-1 outline-none focus:border-white/50 disabled:opacity-40'
const POLL_MS = 10_000

export const LaunchBoard = ({
    canEdit,
    canDeploy,
    onShowServers,
}: {
    /** Executive: writes a kind of server or a schedule. */
    canEdit: boolean
    /** Developer or executive: builds servers from a kind that already exists. */
    canDeploy: boolean
    onShowServers?: (template: Template) => void
}) => {
    const { addNotification } = useNotification()
    const [templates, setTemplates] = useState<Template[]>([])
    const [windows, setWindows] = useState<Window[]>([])
    const [options, setOptions] = useState<Options | null>(null)
    const [deployments, setDeployments] = useState<Deployment[]>([])
    const [rules, setRules] = useState<Rules>(DEFAULT_RULES)
    /** Who last changed the shared rules, and when, for the rules popup. */
    const [rulesMeta, setRulesMeta] = useState<{ updatedAt: string | null; updatedBy: string | null }>({
        updatedAt: null,
        updatedBy: null,
    })
    const [editRules, setEditRules] = useState<null | 'rank' | 'loot'>(null)
    /** One server, or an event's worth. Same box, same map and fee; the
     *  questions after that differ. */
    const [mode, setMode] = useState<'one' | 'event'>('one')
    /* Kill races these servers should join once they come up.
     *
     * Asked here because this is where somebody is already deciding what to
     * build and why. The alternative was: build them, go to Tournaments, find
     * the race, tick five new servers by hand -- and do it again tomorrow. A
     * race's own auto-add rule covers "any NOOB server in EU"; this covers
     * "these ones", which a standing rule cannot say. */
    const [races, setRaces] = useState<JoinableRace[]>([])
    const [joinRaces, setJoinRaces] = useState<number[]>([])
    /** Build now, or have the schedule build it at a time. A time makes a
     *  schedule row (the same one the old Schedules tab made) and the board
     *  lists it under Scheduled servers until it fires. */
    const [when, setWhen] = useState<'now' | 'later'>('now')
    const [deployAt, setDeployAt] = useState('')
    const [destroyAt, setDestroyAt] = useState('')
    const [repeatWeekly, setRepeatWeekly] = useState(false)
    /** Event mode picks several maps. The most preferred of them takes the
     *  main share; the rest split the remainder in preference order. */
    const [eventMaps, setEventMaps] = useState<string[]>([])

    // The three choices, then loot, then where.
    const [mapId, setMapId] = useState<number | null>(null)
    const [feeChoice, setFeeChoice] = useState<string | null>(null)
    const [addingFee, setAddingFee] = useState<string | null>(null)
    const [rank, setRank] = useState<Rank | null>(null)
    const [autoLoot, setAutoLoot] = useState(true)
    const [usdcBalance, setUsdcBalance] = useState('')
    const [pointBalance, setPointBalance] = useState('')
    const [region, setRegion] = useState('')
    const [count, setCount] = useState(1)
    const [machine, setMachine] = useState('default')
    const [confirmDeploy, setConfirmDeploy] = useState(false)
    const [deploying, setDeploying] = useState(false)

    /** The name pattern under the loot row. Blank means the default. */
    const [namePattern, setNamePattern] = useState('')
    /** Max players. Blank means "whatever the map says"; a number means the
     *  operator overrode it for this kind. Follows the picked kind when one
     *  is picked, so editing an existing kind starts from its real value. */
    const [maxPlayers, setMaxPlayers] = useState('')
    /** Blank is a public server. Anything typed makes it private and the
     *  player has to know it to get in.
     *
     *  This lives on the kind, not on the one server, which is what the game
     *  reads -- so it is loaded from the picked kind and the row says plainly
     *  that later servers of that kind inherit it. */
    const [password, setPassword] = useState('')
    const [savingName, setSavingName] = useState(false)

    const [editing, setEditing] = useState<Template | null>(null)
    const [creating, setCreating] = useState<TemplateFormState | null>(null)

    const applySharedRules = (shared?: ServerRules) => {
        setRules(rulesFrom(shared))
        setRulesMeta({ updatedAt: shared?.updatedAt ?? null, updatedBy: shared?.updatedBy ?? null })
    }

    const saveRules = async (given: Rules) => {
        try {
            const { rules: saved } = await saveServerRules(withoutOverlap(given))
            applySharedRules(saved)
            addNotification('Rules saved for everyone', 'success')
        } catch (err: any) {
            addNotification(err.message, 'error')
        }
    }

    const refresh = useCallback(async () => {
        try {
            const [data, opts] = await Promise.all([getTemplates(), getOptions()])
            setTemplates(data.templates)
            setWindows(data.windows ?? [])
            setOptions(opts)
            applySharedRules(opts.rules)
        } catch (err: any) {
            addNotification(err.message, 'error')
        }
    }, [])

    const poll = useCallback(async () => {
        try {
            const { deployments } = await getDeployments()
            setDeployments(deployments)
            return deployments
        } catch {
            return []
        }
    }, [])

    useEffect(() => {
        refresh()
        poll()
    }, [refresh, poll])

    // Once. A race starting while somebody fills in this form is rare enough
    // that polling for it would be a request a minute to learn nothing.
    useEffect(() => {
        getJoinableRaces()
            .then((r) => setRaces(r.races ?? []))
            .catch(() => setRaces([]))
    }, [])

    useEffect(() => {
        if (!deployments.length) return
        const timer = setInterval(async () => {
            const current = await poll()
            if (!current.length) refresh()
        }, POLL_MS)
        return () => clearInterval(timer)
    }, [deployments.length, poll, refresh])

    const maps = pickableMaps(options?.maps ?? [])
    // Free is always offered, whether or not a free kind happens to exist
    // yet. It is a real choice -- an event, a test, a free weekend -- and
    // leaving it out meant typing 0 into "another fee" to reach something
    // the rules already handle. A free server simply gets no loot pools.
    const fees = useMemo(() => {
        const seen = new Set(['0.00', ...templates.map((t) => fee(t.fee))])
        if (feeChoice) seen.add(feeChoice)
        return [...seen].sort((a, b) => Number(a) - Number(b))
    }, [templates, feeChoice])

    // The kind for these three things IN THE REGION PICKED. Several kinds
    // can be the same map, fee and rank ("SA Region Military 0.25" and
    // "Multi Region Military 0.25"); a kind already allowed into the region
    // wins, then one allowed everywhere, then -- only so the screen can
    // say what it would have been -- any of them. Enabled ones first
    // throughout. Without the region, Europe was handed the SA one.
    const match = useMemo(() => {
        if (mapId === null || feeChoice === null || rank === null) return null
        const same = templates.filter(
            (t) => t.map_id === mapId && fee(t.fee) === feeChoice && rankOf(t, rules) === rank
        )
        const ordered = [...same.filter((t) => t.enabled), ...same.filter((t) => !t.enabled)]
        return (
            ordered.find((t) => t.deploy_slot_regions.includes(region)) ??
            ordered.find((t) => (options?.slot_regions ?? []).every((sr) => t.deploy_slot_regions.includes(sr.id))) ??
            ordered[0] ??
            null
        )
    }, [templates, mapId, feeChoice, rank, rules, region, options])
    const chosen = mapId !== null && feeChoice !== null && rank !== null
    const regionLabel = (id: string) => options?.slot_regions.find((s) => s.id === id)?.label ?? id
    const mapName = (id: number | null) => mapLabel(maps.find((m) => m.id === id)?.name)

    // Every region, always. Region is where you put a server, not a
    // property of the kind -- so the dropdown never hides one. A kind that
    // has not been set up for the chosen region says so, with a switch.
    const allRegions = options?.slot_regions.map((s) => s.id) ?? []
    useEffect(() => {
        if (!region && allRegions.length) setRegion(allRegions[0])
    }, [allRegions.join(',')])
    const regionEnabled = !match || match.deploy_slot_regions.includes(region)

    useEffect(() => {
        setNamePattern(match ? match.server_name_template : '')
    }, [match?.id])

    const mapCap = maxPlayersFor(maps.find((m) => m.id === mapId)?.name)
    const maxPlayersValue = maxPlayers.trim() !== '' ? Number(maxPlayers) : mapCap
    // Start from what the kind actually holds, so an existing private kind
    // does not look public and get quietly opened up on the next build.
    useEffect(() => {
        setPassword(match?.password ?? '')
    }, [match?.id])
    const passwordValue = password.trim() === '' ? null : password.trim()

    const pattern = namePattern.trim() || DEFAULT_NAME
    const preview = previewName(pattern, {
        zone: NAME_REGION[region] ?? 'US',
        rank: rank ? RANK_COPY[rank].label.toUpperCase() : 'PUBLIC',
        map: mapName(mapId) || 'Military Base',
        mapName: maps.find((m) => m.id === mapId)?.name,
        fee: feeChoice ?? '',
        // The SAME code the backend renders, not the dropdown's label.
        //
        // This read `regionLabel(region)`, which is the friendly name the
        // picker shows -- "Europe", "South East Asia". The backend renders
        // `{region}` as the slot's code, so the preview promised "Europe #1"
        // and the machine came up "EU #1". Three vocabularies were in play at
        // once: the code, the picker's label, and the region the game client
        // is told. Only the first belongs in a name.
        region: NAME_REGION[region] ?? 'US',
    })

    const saveName = async () => {
        if (!match) return
        setSavingName(true)
        try {
            await saveTemplate(match.id, { server_name_template: pattern })
            addNotification(`${match.name} will be named "${preview.rendered}"`, 'success')
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setSavingName(false)
            refresh()
        }
    }

    const enableRegion = async () => {
        if (!match) return
        try {
            await saveTemplate(match.id, { deploy_slot_regions: [...match.deploy_slot_regions, region] })
            addNotification(`${match.name} can now go to ${regionLabel(region)}`, 'success')
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            refresh()
        }
    }

    // What the loot row shows: the rules' answer for this fee, unless the
    // operator has switched to typing balances.
    const loot = useMemo(
        () =>
            feeChoice === null
                ? []
                : lootFor(feeChoice, rules, autoLoot ? undefined : { usdc: usdcBalance, point: pointBalance }),
        [feeChoice, rules, autoLoot, usdcBalance, pointBalance]
    )
    const matchLootIsAuto = match ? samePools(match.loot_pools, lootFor(fee(match.fee), rules)) : true

    const scheduled = when === 'later'
    const scheduleReady = !scheduled || Boolean(deployAt)

    const deploy = async () => {
        if (!match) return
        setDeploying(true)
        try {
            // The box wins. A kind carrying 25 from whenever it was made must
            // not put 25 on a map that holds 15 because nobody noticed the
            // row; the number on screen is the number the servers get.
            if (maxPlayersValue !== null && maxPlayersValue !== match.max_players) {
                await saveTemplate(match.id, { max_players: maxPlayersValue })
            }
            // Same rule as max players: the box wins. Blank is public.
            if (passwordValue !== (match.password || null)) {
                await saveTemplate(match.id, { password: passwordValue })
            }
            if (scheduled) {
                await saveWindow(null, {
                    // Unique and capped: DeploymentWindow.name is unique, 64 chars,
                    // so the same schedule for next week was refused as a duplicate.
                    name: `${count} × ${match.name} in ${regionLabel(region)} @ ${(deployAt || '').slice(0, 16)}`.slice(
                        0,
                        64
                    ),
                    enabled: true,
                    templates: [{ profile_id: match.id, count, region }],
                    deploy_at: deployAt || null,
                    destroy_at: destroyAt || null,
                    repeat_weekly: repeatWeekly,
                })
                addNotification(`${count} × ${match.name} scheduled for ${showInZone(deployAt, getZone())}`, 'success')
            } else {
                await deployTemplate(
                    match.id,
                    count,
                    region || undefined,
                    machine === 'default' ? undefined : machine,
                    joinRaces
                )
                addNotification(
                    `${count} × ${match.name} requested in ${regionLabel(region)}` +
                        (joinRaces.length
                            ? `, joining ${joinRaces.length} kill race${joinRaces.length === 1 ? '' : 's'} once built`
                            : ''),
                    'success'
                )
            }
            setConfirmDeploy(false)
            await poll()
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setDeploying(false)
        }
    }

    /** Write the loot row onto the matched template, so what the page shows
     *  is what the next server built from it gets. */
    const applyLoot = async () => {
        if (!match) return
        try {
            await saveTemplate(match.id, { loot_pools: loot })
            addNotification(`Loot on ${match.name} updated`, 'success')
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            refresh()
        }
    }

    /** A new template from the three choices and the loot row: a name and
     *  Save, not a twenty-field form. */
    /** The form state a kind of server is made from: map, fee, rank, and
     *  everything the rules derive. Shared by "set it up" on the one-server
     *  side and the event planner, which makes missing kinds without asking. */
    const kindForm = (map: number, feeValue: string, r: Rank): TemplateFormState => {
        const label = RANK_COPY[r].label
        return {
            ...blankForm(),
            name: `${label} ${mapName(map)} ${money(feeValue)}`,
            map_id: map,
            fee: feeValue,
            // The defaults, not what is typed on the one-server side: event mode
            // makes these silently, and a 4 left in Max players there was
            // becoming the size of every kind an event created.
            server_name_template: DEFAULT_NAME,
            deploy_slot_regions: options?.slot_regions.map((s) => s.id) ?? [],
            loot_pools: lootFor(feeValue, rules),
            max_players: String(maxPlayersFor(maps.find((m) => m.id === map)?.name) ?? blankForm().max_players),
            ...requirementsFor(r, rules),
        }
    }

    /** Make the kind now, silently, and return it. An event does not stop
     *  to ask whether a $1.00 Radio Station has ever existed; it makes one. */
    const ensureKind = async (map: number, feeValue: string, r: Rank): Promise<Template> => {
        const form = kindForm(map, feeValue, r)
        // ServerProfile.name is unique, and the event planner will not reuse a
        // kind that is not allowed into the region asking for it -- reusing one
        // is how a US event got servers named "SA #1". So "make a fresh kind"
        // can land on a name a kind already holds, and the build stops on a
        // database error about a duplicate name instead of on something the
        // operator can act on. Name what is actually in the way.
        const clash = templates.find((t) => t.name === form.name)
        if (clash) {
            throw new Error(
                `A kind called "${clash.name}" already exists but cannot deploy into this region. ` +
                    `In Templates, add the region to it or rename it, then build again.`
            )
        }
        const saved = await saveTemplate(null, payloadOf(form))
        const fresh = await getTemplates()
        setTemplates(fresh.templates)
        const found =
            fresh.templates.find((t) => t.id === (saved?.id ?? saved?.template?.id)) ??
            // Newest look-alike, not the first: the first is whatever was
            // there before, which is the one this was made to avoid.
            [...fresh.templates]
                .reverse()
                .find((t) => t.map_id === map && fee(t.fee) === feeValue && rankOf(t, rules) === r)
        if (!found) throw new Error(`Could not set up ${RANK_COPY[r].label} ${mapName(map)} ${money(feeValue)}`)
        return found
    }

    const startKind = (map: number, feeValue: string, r: Rank) => {
        const label = RANK_COPY[r].label
        setCreating({
            ...blankForm(),
            name: `${label} ${mapName(map)} ${money(feeValue)}`,
            map_id: map,
            fee: feeValue,
            server_name_template: namePattern.trim() || DEFAULT_NAME,
            deploy_slot_regions: options?.slot_regions.map((s) => s.id) ?? [],
            loot_pools: lootFor(feeValue, rules),
            // The map decides. Fifteen on Military Base, two in the Cage;
            // a box for it would only ever be typed wrong.
            max_players: String(
                (map === mapId && maxPlayers.trim() !== '' ? Number(maxPlayers) : null) ??
                    maxPlayersFor(maps.find((m) => m.id === map)?.name) ??
                    blankForm().max_players
            ),
            ...requirementsFor(r, rules),
        })
    }

    const startTemplate = () => {
        if (mapId === null || feeChoice === null || rank === null) return
        startKind(mapId, feeChoice, rank)
    }

    return (
        <div className='flex flex-col gap-4 text-xs font-mono'>
            {
                <div className='flex flex-col gap-3 rounded border border-violet-400/30 bg-violet-400/[0.03] p-3'>
                    <div className='flex flex-row items-center justify-between'>
                        <span className='flex flex-row items-center gap-3'>
                            <span className='text-[10px] uppercase tracking-widest text-violet-300/80'>
                                {mode === 'one' ? 'Make a server' : 'Make servers for an event'}
                            </span>
                            <span className='flex flex-row overflow-hidden rounded-md border border-violet-400/50'>
                                {(
                                    [
                                        ['one', 'One server'],
                                        ['event', 'Event'],
                                    ] as const
                                ).map(([key, label]) => (
                                    <button
                                        key={key}
                                        type='button'
                                        onClick={() => setMode(key)}
                                        className={`px-5 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                                            mode === key
                                                ? 'bg-violet-600 text-white'
                                                : 'text-white/50 hover:bg-white/5 hover:text-white'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </span>
                        </span>
                    </div>

                    {true && (
                        <Choice label={mode === 'one' ? 'Map' : 'Maps'}>
                            {maps.map((m) => (
                                <Chip
                                    key={m.id}
                                    on={mode === 'one' ? mapId === m.id : eventMaps.includes(m.name)}
                                    onClick={() =>
                                        mode === 'one'
                                            ? setMapId(m.id)
                                            : setEventMaps((list) =>
                                                  list.includes(m.name)
                                                      ? list.filter((x) => x !== m.name)
                                                      : [...list, m.name]
                                              )
                                    }
                                    onStyle='border-sky-400/70 bg-sky-400/10 text-sky-300'
                                >
                                    {mapLabel(m.name)}
                                    {maxPlayersFor(m.name) !== null && (
                                        <span className='ml-1.5 normal-case tracking-normal text-white/30'>
                                            {maxPlayersFor(m.name)} players
                                        </span>
                                    )}
                                </Chip>
                            ))}
                            {mode === 'event' && (
                                <span className='text-[10px] text-white/30'>
                                    pick several; the first by preference (Military Base, Radio Station, Cage, Hangar)
                                    takes 80%, the rest share the remainder
                                </span>
                            )}
                        </Choice>
                    )}

                    {mode === 'one' && (
                        <Choice label='Spawn fee'>
                            {fees.map((f) => (
                                <Chip
                                    key={f}
                                    on={feeChoice === f}
                                    onClick={() => setFeeChoice(f)}
                                    onStyle='border-violet-400/70 bg-violet-400/10 text-violet-300'
                                >
                                    {money(f)}
                                </Chip>
                            ))}
                            {addingFee === null ? (
                                <button
                                    type='button'
                                    onClick={() => setAddingFee('')}
                                    title='Another fee'
                                    className='rounded border border-dashed border-white/20 px-2 py-1 text-[10px] text-white/40 hover:border-white/40 hover:text-white'
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            ) : (
                                <span className='flex flex-row items-center gap-1'>
                                    <span className='text-white/40'>$</span>
                                    <input
                                        autoFocus
                                        inputMode='decimal'
                                        value={addingFee}
                                        onChange={(e) => setAddingFee(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && addingFee !== '' && Number(addingFee) >= 0) {
                                                setFeeChoice(fee(addingFee))
                                                setAddingFee(null)
                                            }
                                            if (e.key === 'Escape') setAddingFee(null)
                                        }}
                                        placeholder='0.75'
                                        className={`${INPUT} w-16 py-0.5`}
                                    />
                                    <span className='text-[10px] text-white/30'>enter</span>
                                </span>
                            )}
                            {/* Read-only (D-090): the split is edited in the admin, not
                                here. It says how much of the fee comes off AIRDROP. */}
                            {options?.fee_split && (
                                <span
                                    className='ml-2 text-[10px] text-white/40'
                                    title='How a paid spawn fee is taken from the player. If they hold too little of one currency, the other covers the shortfall. Changed in the admin (Join Fee Split).'
                                >
                                    paid {Number(options.fee_split.airdrop_percent)}% AIRDROP /{' '}
                                    {Number(options.fee_split.usdc_percent)}% USDC
                                </span>
                            )}
                        </Choice>
                    )}

                    {mode === 'one' && (
                        <Choice label='Rank'>
                            {RANKS.map((r) => (
                                <Chip
                                    key={r}
                                    on={rank === r}
                                    onClick={() => setRank(r)}
                                    onStyle={RANK_COPY[r].on}
                                    title={rankHint(r, rules)}
                                >
                                    {RANK_COPY[r].label}
                                    <span className='ml-1.5 normal-case tracking-normal text-white/30'>
                                        {rankHint(r, rules)}
                                    </span>
                                </Chip>
                            ))}
                            <EditLink onClick={() => setEditRules('rank')} />
                        </Choice>
                    )}

                    {/* Loot: nothing to type unless you want to. */}
                    {mode === 'one' && (
                        <Choice label='Max players'>
                            <input
                                type='number'
                                min={1}
                                max={64}
                                value={maxPlayers}
                                onChange={(e) => setMaxPlayers(e.target.value)}
                                placeholder={mapCap !== null ? String(mapCap) : ''}
                                className={`${INPUT} w-20 py-0.5 text-right tabular-nums`}
                            />
                            <span className='text-[10px] text-white/30'>
                                {mapId === null
                                    ? 'pick a map'
                                    : maxPlayers.trim() === ''
                                      ? `the map's number (${mapCap ?? 'no cap'})`
                                      : mapCap !== null && Number(maxPlayers) !== mapCap
                                        ? `overriding the map's ${mapCap}`
                                        : 'same as the map'}
                            </span>
                            {maxPlayers.trim() !== '' && (
                                <button
                                    type='button'
                                    onClick={() => setMaxPlayers('')}
                                    className='text-[10px] uppercase tracking-widest text-white/40 underline hover:text-white'
                                >
                                    use the map's
                                </button>
                            )}
                            {match && maxPlayersValue !== match.max_players && (
                                <span className='text-[10px] text-violet-300'>
                                    this kind currently says {match.max_players ?? 'no cap'} — Make will set it to{' '}
                                    {maxPlayersValue ?? 'no cap'}
                                </span>
                            )}
                        </Choice>
                    )}

                    {mode === 'one' && (
                        <Choice label='Password'>
                            <input
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                maxLength={64}
                                placeholder='leave blank for a public server'
                                className={`${INPUT} w-60 py-0.5`}
                            />
                            <span className='text-[10px] text-white/30'>
                                {passwordValue === null
                                    ? 'anyone can join'
                                    : 'private — players need this to get in'}
                            </span>
                            {passwordValue !== null && (
                                <button
                                    type='button'
                                    onClick={() => setPassword('')}
                                    className='text-[10px] uppercase tracking-widest text-white/40 underline hover:text-white'
                                >
                                    make it public
                                </button>
                            )}
                            {match && passwordValue !== (match.password || null) && (
                                <span className='text-[10px] text-violet-300'>
                                    this kind is currently {match.password ? 'private' : 'public'} — Make will set it
                                    to {passwordValue === null ? 'public' : 'private'}, including servers made from it
                                    later
                                </span>
                            )}
                        </Choice>
                    )}

                    {mode === 'one' && (
                        <Choice label='Loot'>
                            {feeChoice === null ? (
                                <span className='text-white/30'>pick a fee first</span>
                            ) : (
                                <>
                                    <label className='flex cursor-pointer flex-row items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/60'>
                                        <input
                                            type='checkbox'
                                            checked={autoLoot}
                                            onChange={(e) => setAutoLoot(e.target.checked)}
                                        />
                                        auto
                                    </label>
                                    <PoolBox
                                        label='USDC'
                                        balance={
                                            autoLoot
                                                ? (loot.find((p) => p.currency === 'USDC')?.amount ?? '0.00')
                                                : usdcBalance
                                        }
                                        onBalance={setUsdcBalance}
                                        pool={loot.find((p) => p.currency === 'USDC')}
                                        auto={autoLoot}
                                    />
                                    <PoolBox
                                        label='Points'
                                        balance={
                                            autoLoot
                                                ? (loot.find((p) => p.currency === 'POINT')?.amount ?? '0.00')
                                                : pointBalance
                                        }
                                        onBalance={setPointBalance}
                                        pool={loot.find((p) => p.currency === 'POINT')}
                                        auto={autoLoot}
                                    />
                                    {loot.length === 0 && (
                                        <span className='text-white/30'>free servers carry no pools</span>
                                    )}
                                    <EditLink onClick={() => setEditRules('loot')} />
                                </>
                            )}
                        </Choice>
                    )}

                    {mode === 'one' && (
                        <Choice label='Name'>
                            <input
                                value={namePattern}
                                onChange={(e) => setNamePattern(e.target.value)}
                                placeholder={DEFAULT_NAME}
                                className={`${INPUT} w-72 py-0.5`}
                            />
                            <span
                                className={`tabular-nums ${preview.unknown.length || preview.tooLong ? 'text-red-400' : 'text-white/60'}`}
                            >
                                → {preview.rendered || '…'}
                                {preview.unknown.length > 0 && ` (unknown: ${preview.unknown.join(', ')})`}
                                {preview.tooLong && ' (too long)'}
                            </span>
                            {match && pattern !== match.server_name_template && canEdit && (
                                <button
                                    type='button'
                                    onClick={saveName}
                                    disabled={savingName || preview.unknown.length > 0 || preview.tooLong}
                                    className='text-[10px] uppercase tracking-widest text-violet-300 underline disabled:opacity-40'
                                >
                                    {savingName ? 'saving…' : 'save name'}
                                </button>
                            )}
                            <span className='text-[10px] text-white/30'>{'{zone} {rank} {map} {index} {fee}'}</span>
                        </Choice>
                    )}

                    <Choice label='When'>
                        <Chip
                            on={when === 'now'}
                            onClick={() => setWhen('now')}
                            onStyle='border-violet-400/70 bg-violet-400/10 text-violet-300'
                        >
                            now
                        </Chip>
                        <Chip
                            on={when === 'later'}
                            onClick={() => setWhen('later')}
                            onStyle='border-violet-400/70 bg-violet-400/10 text-violet-300'
                        >
                            at a time
                        </Chip>
                        {when === 'later' && (
                            <span className='flex flex-col gap-1.5'>
                                <DateTimeField label='build' value={deployAt} onChange={(v) => setDeployAt(v ?? '')} />
                                <DateTimeField
                                    label='tear down'
                                    value={destroyAt}
                                    onChange={(v) => setDestroyAt(v ?? '')}
                                />
                                <span className='flex flex-row items-center gap-3'>
                                    <span className='text-[10px] text-white/30'>blank tear-down keeps them up</span>
                                    <label className='flex cursor-pointer flex-row items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/60'>
                                        <input
                                            type='checkbox'
                                            checked={repeatWeekly}
                                            onChange={(e) => setRepeatWeekly(e.target.checked)}
                                        />
                                        every week
                                    </label>
                                </span>
                            </span>
                        )}
                    </Choice>

                    {/* Only when there is a race to join. A row offering
                        nothing is a row to read past on every visit, and most
                        servers are not built for an event. */}
                    {when === 'now' && races.length > 0 && (
                        <Choice label='Kill race'>
                            {races.map((race) => {
                                const on = joinRaces.includes(race.id)
                                return (
                                    <Chip
                                        key={race.id}
                                        on={on}
                                        onClick={() =>
                                            setJoinRaces((current) =>
                                                on
                                                    ? current.filter((id) => id !== race.id)
                                                    : [...current, race.id]
                                            )
                                        }
                                        onStyle={
                                            race.running
                                                ? 'border-green-400/70 bg-green-400/10 text-green-400'
                                                : 'border-amber-400/70 bg-amber-400/10 text-amber-300'
                                        }
                                    >
                                        {race.name}
                                        <span className='pl-1.5 opacity-60'>
                                            {race.running ? 'live' : 'scheduled'}
                                            {race.enabled ? '' : ' · off'}
                                        </span>
                                    </Chip>
                                )
                            })}
                            <span className='text-[10px] text-white/45'>
                                {joinRaces.length === 0
                                    ? 'optional — join none and they just run normally'
                                    : `they join ${joinRaces.length} race${
                                          joinRaces.length === 1 ? '' : 's'
                                      } as soon as they come up`}
                            </span>
                        </Choice>
                    )}

                    {mode === 'one' && (
                        <>
                            {/* What the choices add up to, then where. */}
                            <div className='flex flex-row flex-wrap items-end gap-3 border-t border-white/10 pt-3'>
                                <div className='flex min-w-[16rem] flex-1 flex-col gap-1'>
                                    <span className='text-[10px] uppercase tracking-widest text-white/50'>That is</span>
                                    {!chosen ? (
                                        <span className='text-white/30'>pick a map, a fee and a rank</span>
                                    ) : match ? (
                                        <span className='flex flex-row flex-wrap items-baseline gap-x-2'>
                                            <span className='font-bold'>{match.name}</span>
                                            <span className='text-white/40'>
                                                named like "{match.example_name}" · {match.max_players ?? 'no cap'} max{' '}
                                                · running on{' '}
                                                <button
                                                    type='button'
                                                    onClick={() => match.running_servers > 0 && onShowServers?.(match)}
                                                    className={
                                                        match.running_servers > 0 ? 'text-green-400 underline' : ''
                                                    }
                                                >
                                                    {match.running_servers}
                                                </button>
                                            </span>
                                            {!matchLootIsAuto && (
                                                <span className='text-violet-300'>
                                                    its loot is hand-set
                                                    {canEdit && (
                                                        <button
                                                            type='button'
                                                            onClick={applyLoot}
                                                            className='ml-1 underline'
                                                        >
                                                            use the loot above
                                                        </button>
                                                    )}
                                                </span>
                                            )}
                                            <button
                                                type='button'
                                                onClick={() => setEditing(match)}
                                                className='text-white/40 underline hover:text-white'
                                            >
                                                settings
                                            </button>
                                        </span>
                                    ) : (
                                        <span className='flex flex-row flex-wrap items-center gap-2'>
                                            <span className='text-violet-300'>
                                                that kind of server does not exist yet
                                            </span>
                                            <ActionButton
                                                onClick={async () => startTemplate()}
                                                disabled={!canEdit}
                                                className='text-violet-300'
                                            >
                                                <FontAwesomeIcon icon={faPlus} className='mr-1' /> Set it up
                                            </ActionButton>
                                        </span>
                                    )}
                                </div>

                                <label className='flex flex-col gap-1'>
                                    <span className='text-[10px] uppercase tracking-widest text-white/50'>Region</span>
                                    <select
                                        value={region}
                                        onChange={(e) => setRegion(e.target.value)}
                                        disabled={!match}
                                        className={INPUT}
                                    >
                                        {allRegions.map((r) => (
                                            <option key={r} value={r} className='bg-neutral-900'>
                                                {regionLabel(r)}
                                                {match && !match.deploy_slot_regions.includes(r) ? ' (not set up)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className='flex flex-col gap-1'>
                                    <span className='text-[10px] uppercase tracking-widest text-white/50'>
                                        How many
                                    </span>
                                    <input
                                        type='number'
                                        min={1}
                                        max={20}
                                        value={count}
                                        onChange={(e) =>
                                            setCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))
                                        }
                                        disabled={!match}
                                        className={`${INPUT} w-16 text-right`}
                                    />
                                </label>
                                <label className='flex flex-col gap-1'>
                                    <span className='text-[10px] uppercase tracking-widest text-white/50'>Machine</span>
                                    <select
                                        value={machine}
                                        onChange={(e) => setMachine(e.target.value)}
                                        disabled={!match}
                                        className={INPUT}
                                    >
                                        {MACHINE_TYPES.map((m) => (
                                            <option key={m.value} value={m.value} className='bg-neutral-900'>
                                                {m.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {match && !regionEnabled ? (
                                    <ActionButton
                                        onClick={enableRegion}
                                        disabled={!canEdit}
                                        className='border-violet-400/60 text-violet-300'
                                        title={`This kind has not been set up for ${regionLabel(region)} yet. Switch it on, then make it.`}
                                    >
                                        Enable {regionLabel(region)} for this kind
                                    </ActionButton>
                                ) : (
                                    <ActionButton
                                        onClick={async () => setConfirmDeploy(true)}
                                        // A switched-off kind is retired: the old Deploy
                                        // button refused it and so does this one.
                                        // Building now is DeveloperPermission; a schedule
                                        // writes a window, which is executive.
                                        disabled={
                                            !match ||
                                            !match.enabled ||
                                            !(scheduled ? canEdit : canDeploy) ||
                                            deploying ||
                                            !scheduleReady
                                        }
                                        title={
                                            match && !match.enabled
                                                ? `${match.name} is switched off in Templates.`
                                                : undefined
                                        }
                                        className='border-violet-500 bg-violet-600 px-5 py-1.5 font-bold text-white enabled:hover:bg-violet-500'
                                    >
                                        <FontAwesomeIcon icon={faRocket} className='mr-1' />{' '}
                                        {scheduled
                                            ? `Schedule ${count > 1 ? count : 'it'}`
                                            : `Make ${count > 1 ? count : 'it'}`}
                                    </ActionButton>
                                )}
                            </div>
                        </>
                    )}

                    {/* Options may not have loaded (or failed to). The planner
                        reads options.slot_regions straight away, so rendering it
                        without them threw and blanked Server Control. */}
                    {mode === 'event' && options && (
                        <EventPlanner
                            templates={templates}
                            options={options}
                            maps={maps}
                            eventMaps={eventMaps}
                            schedule={scheduled ? { deployAt, destroyAt, repeatWeekly } : null}
                            rules={rules}
                            canEdit={canEdit}
                            canDeploy={canDeploy}
                            onEnsureKind={ensureKind}
                            onDone={() => {
                                poll()
                                refresh()
                            }}
                            windows={windows}
                            joinRaces={when === 'now' ? joinRaces : []}
                            onEditLoot={() => setEditRules('loot')}
                        />
                    )}

                    {mode === 'one' && confirmDeploy && match && (
                        <ConfirmBar
                            message={`Build ${count} ${RANK_COPY[rank!].label.toLowerCase()} ${mapName(mapId)} at ${money(
                                feeChoice
                            )} in ${regionLabel(region)}?`}
                            detail={`Each is a real machine on GCP, named like "${match.example_name}". A few minutes; it shows below while it builds.`}
                            confirmLabel={`Build ${count}`}
                            busy={deploying}
                            onCancel={() => setConfirmDeploy(false)}
                            onConfirm={deploy}
                        />
                    )}

                    {deployments.length > 0 && (
                        <div className='flex flex-col gap-1 border-t border-white/10 pt-2'>
                            {deployments.map((d) => {
                                const label = runLabel(d)
                                return (
                                    <div key={d.slot} className='flex flex-row items-center gap-3'>
                                        <span className='w-10 text-white/40'>{d.slot}</span>
                                        <span className='min-w-0 flex-1 truncate'>{d.template ?? 'template'}</span>
                                        <span className={label.style}>{label.text}</span>
                                        <span className='text-white/30'>
                                            {Math.round(d.age_seconds / 60)}m · {d.requested_by ?? '—'}
                                        </span>
                                        {d.run_url && (
                                            <a
                                                href={d.run_url}
                                                target='_blank'
                                                rel='noreferrer'
                                                className='text-white/40 underline hover:text-white'
                                            >
                                                run
                                            </a>
                                        )}
                                        {d.run_conclusion === 'failure' && (
                                            <ActionButton
                                                disabled={!canDeploy}
                                                onClick={async () => {
                                                    // By id: the slot ("eu-004") through Number()
                                                    // was NaN, so this always 404'd silently.
                                                    try {
                                                        await dismissDeployment(d.id)
                                                    } catch (err: any) {
                                                        addNotification(err.message, 'error')
                                                    } finally {
                                                        poll()
                                                    }
                                                }}
                                                className='text-white/50'
                                            >
                                                dismiss
                                            </ActionButton>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            }

            {editRules && (
                <RulesSheet
                    section={editRules}
                    rules={rules}
                    rulesMeta={rulesMeta}
                    current={match}
                    currentLoot={loot}
                    canEdit={canEdit}
                    onSave={saveRules}
                    onSavedKind={refresh}
                    onClose={() => setEditRules(null)}
                />
            )}

            {editing && options && (
                <TemplateSheet
                    template={editing}
                    options={options}
                    canEdit={canEdit}
                    rules={rules}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null)
                        refresh()
                    }}
                />
            )}

            {creating && options && (
                <div
                    className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4'
                    onClick={() => setCreating(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className='max-h-[88vh] w-full max-w-3xl overflow-auto rounded border border-white/20 bg-zinc-900 p-4 text-xs font-mono'
                    >
                        <TemplateEditor
                            options={options}
                            canEdit={canEdit}
                            seed={creating}
                            onSaved={() => {
                                setCreating(null)
                                refresh()
                            }}
                            onCancel={() => setCreating(null)}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

const EditLink = ({ onClick }: { onClick: () => void }) => (
    <button
        type='button'
        onClick={onClick}
        className='ml-1 flex flex-row items-center gap-1 text-[10px] uppercase tracking-widest text-white/40 underline hover:text-white'
    >
        <FontAwesomeIcon icon={faSliders} /> edit
    </button>
)

/** An event's worth of servers in one go.
 *
 *  "Fifty people, EU and NA, noob and pro" is how an event is described,
 *  and this turns that sentence into a plan: the people split evenly across
 *  every region x rank, each share divided by the map's player cap, rounded
 *  up so nobody is left without a slot. The plan is on screen before
 *  anything is built -- one row per kind and region, saying how many and
 *  whether that kind exists yet -- and Make all fires one deploy per row.
 */
const EventPlanner = ({
    templates,
    options,
    maps,
    eventMaps,
    schedule,
    rules,
    canEdit,
    canDeploy,
    onEnsureKind,
    onDone,
    windows,
    joinRaces,
    onEditLoot,
}: {
    templates: Template[]
    options: Options
    maps: Array<{ id: number; name: string }>
    eventMaps: string[]
    /** Build at this time instead of now. */
    schedule: { deployAt: string; destroyAt: string; repeatWeekly: boolean } | null
    rules: Rules
    canEdit: boolean
    canDeploy: boolean
    onEnsureKind: (map: number, feeValue: string, rank: Rank) => Promise<Template>
    onDone: () => void
    /** Schedules already made, so an event can be added to one. */
    windows: Window[]
    /** Kill races every server built now should join, picked on the Kill race row. */
    joinRaces: number[]
    onEditLoot: () => void
}) => {
    const { addNotification } = useNotification()
    const [people, setPeople] = useState('50')
    const [zones, setZones] = useState<string[]>([])
    const [ranks, setRanks] = useState<Rank[]>([])
    const [machine, setMachine] = useState('default')
    const [confirm, setConfirm] = useState(false)
    const [building, setBuilding] = useState(false)
    // Add these servers to schedules already made, rather than a new one. An
    // event is saved as one schedule per region, all with the same times, so
    // the choice is a set of times: every schedule sharing them.
    const [addTo, setAddTo] = useState<string | null>(null)
    const timesOf = (w: Window) => `${w.deploy_at}|${w.destroy_at ?? ''}|${w.repeat_weekly}`
    // Only schedules that have not built yet: servers added to one that has
    // already run would wait for a next run that a one-off never gets.
    const openEvents = useMemo(() => {
        const now = Date.now()
        const by = new Map<string, Window[]>()
        for (const w of windows) {
            if (!w.enabled || w.live) continue
            if (!w.repeat_weekly && new Date(w.deploy_at).getTime() <= now) continue
            by.set(timesOf(w), [...(by.get(timesOf(w)) ?? []), w])
        }
        return [...by.entries()]
            .map(([key, list]) => ({
                key,
                list,
                servers: list.reduce((sum, w) => sum + w.total_servers, 0),
                at: list[0].deploy_at,
                repeat: list[0].repeat_weekly,
            }))
            .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
    }, [windows])
    const target = schedule ? openEvents.find((e) => e.key === addTo) ?? null : null

    const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])
    // Every spawn fee this event's maps are dealt at, smallest first.
    const eventFees = Array.from(new Set(eventMaps.flatMap((m) => (MAP_FEES[m] ?? []).map((f) => f.fee)))).sort(
        (a, b) => Number(a) - Number(b)
    )
    const regionLabel = (id: string) => options.slot_regions.find((s) => s.id === id)?.label ?? id
    const mapIdOf = (name: string) => maps.find((m) => m.name === name)?.id ?? null

    // No rank picked means one plain public tier. No region picked means
    // nothing: a region is where the servers go, and guessing one is how
    // fifty servers land in the wrong continent.
    const tiers: Rank[] = ranks.length ? ranks : ['public']
    const n = Math.max(0, Math.floor(Number(people) || 0))
    const combos = zones.length * tiers.length
    const share = combos ? n / combos : 0

    /** Largest-remainder split of `total` whole things across `weights`. */
    const apportion = (total: number, weights: number[], spin = 0): number[] => {
        const sum = weights.reduce((a, b) => a + b, 0) || 1
        const exact = weights.map((w) => (total * w) / sum)
        const counts = exact.map((v) => Math.floor(v))
        let left = total - counts.reduce((a, b) => a + b, 0)
        const order = exact
            .map((v, i) => ({ i, frac: v - Math.floor(v), turn: (i - spin + weights.length * 8) % weights.length }))
            .sort((a, b) => b.frac - a.frac || a.turn - b.turn)
        for (const { i } of order) {
            if (left <= 0) break
            counts[i] += 1
            left -= 1
        }
        return counts
    }

    // The event as a whole first, then dealt out to the groups.
    //
    // Working per region rounded every region up to its own copy of every
    // map, which turned an 80/20 mix into 50/50 and left every region on the
    // same fee. So: how many servers the event needs, from the main map's
    // cap; those split across the maps by SERVER COUNT (80% of the servers
    // are the main map, which is what "80%" means to whoever said it); each
    // map's servers split across its fees by weight; then the resulting list
    // of servers is dealt round-robin across region x rank, so two regions
    // that each get one Military Base get it at different fees. If the seats
    // come up short of the people, more main-map servers are added until
    // they do not.
    const mix = eventMix(eventMaps)
    const mainMap = MAP_PREFERENCE.find((m) => eventMaps.includes(m)) ?? null
    const mapsInMix = MAP_PREFERENCE.filter((m) => eventMaps.includes(m) && MAP_FEES[m])
    const mapShare = (m: string) => mix.filter((e) => e.map === m).reduce((s, e) => s + e.weight, 0)

    const buildList = (): Array<{ mapName: string; fee: string }> => {
        if (!mainMap || n === 0 || combos === 0) return []
        const mainCap = maxPlayersFor(mainMap) ?? 20
        let total = Math.max(combos, Math.ceil(n / mainCap))
        for (let guard = 0; guard < 50; guard += 1) {
            const perMap = apportion(total, mapsInMix.map(mapShare))
            const list: Array<{ mapName: string; fee: string }> = []
            mapsInMix.forEach((m, mi) => {
                const fees = MAP_FEES[m]
                const perFee = apportion(
                    perMap[mi],
                    fees.map((f) => f.weight)
                )
                fees.forEach((f, fi) => {
                    for (let k = 0; k < perFee[fi]; k += 1) list.push({ mapName: m, fee: f.fee })
                })
            })
            const seats = list.reduce((s, x) => s + (maxPlayersFor(x.mapName) ?? 20), 0)
            if (seats >= n) return list
            total += 1
        }
        return []
    }

    const groups = zones.flatMap((z) => tiers.map((r) => ({ zone: z, rank: r })))
    const dealt = new Map<string, number>()
    const list = buildList()
    // Deal the main map first, then the rest, each round-robin from a
    // different starting group so no region always gets the cheap one.
    ;[...list]
        .sort((a, b) => mapsInMix.indexOf(a.mapName) - mapsInMix.indexOf(b.mapName))
        .forEach((srv, i) => {
            const g = groups[i % groups.length]
            const key = `${g.zone}|${g.rank}|${srv.mapName}|${srv.fee}`
            dealt.set(key, (dealt.get(key) ?? 0) + 1)
        })

    // What the operator changed on a line, keyed by the line as it was
    // computed. The plan is recomputed on every keystroke above; edits are
    // reapplied on top so changing the people count does not wipe out a
    // fee somebody just picked by hand. Any changed line shows a "reset".
    type Edit = { zone?: string; rank?: Rank; mapName?: string; fee?: string }
    const [edits, setEdits] = useState<Record<string, Edit>>({})
    const keyOf = (r: { zone: string; rank: Rank; mapName: string; fee: string }) =>
        `${r.zone}|${r.rank}|${r.mapName}|${r.fee}`
    /** The kind to build from, for this map, fee, rank -- IN THIS REGION.
     *
     *  Several kinds can be the same map, fee and rank: the fleet has an
     *  "SA Region Military 0.25" and a "Multi Region Military 0.25", both
     *  public Military Base at a quarter. Picking the first and then adding
     *  the region to it is how a US event got servers named "SA #1". So:
     *  a kind already allowed into this region wins; failing that, one
     *  allowed everywhere; failing that, nothing -- and the build makes a
     *  fresh multi-region kind rather than rewriting somebody's regional
     *  one. */
    const kindFor = (mapName: string, feeValue: string, r: Rank, zone?: string) => {
        const map = mapIdOf(mapName)
        if (map === null) return null
        const same = templates.filter((k) => k.map_id === map && fee(k.fee) === feeValue && rankOf(k, rules) === r)
        // Enabled kinds first; a disabled twin only if nothing else exists,
        // so the build can refuse it rather than try to recreate a name
        // that is already taken.
        const ordered = [...same.filter((k) => k.enabled), ...same.filter((k) => !k.enabled)]
        if (!zone) return ordered[0] ?? null
        return (
            ordered.find((k) => k.deploy_slot_regions.includes(zone)) ??
            ordered.find((k) => options.slot_regions.every((sr) => k.deploy_slot_regions.includes(sr.id))) ??
            null
        )
    }

    const computed = groups.flatMap((g) =>
        mapsInMix.flatMap((m) =>
            MAP_FEES[m].flatMap((f) => {
                const count = dealt.get(`${g.zone}|${g.rank}|${m}|${f.fee}`) ?? 0
                if (!count) return []
                const map = mapIdOf(m)
                const kind = kindFor(m, f.fee, g.rank, g.zone)
                return [
                    {
                        zone: g.zone,
                        rank: g.rank,
                        map,
                        mapName: m,
                        fee: f.fee,
                        count,
                        kind,
                        enabled: kind ? kind.deploy_slot_regions.includes(g.zone) : false,
                    },
                ]
            })
        )
    )
    // One line per server. A line reading "x 2" is one server to the eye,
    // and the ask was to change any single server on its own. Extra lines
    // can be added; any line can be removed. Edits are keyed by the line's
    // computed identity so a change of people above does not wipe them.
    /** One password for the whole event. Blank leaves every kind as it is,
     *  rather than quietly opening up a kind somebody made private. */
    const [password, setPassword] = useState('')
    const [added, setAdded] = useState<Array<{ key: string; zone: string; rank: Rank; mapName: string; fee: string }>>(
        []
    )
    const [removed, setRemoved] = useState<Record<string, true>>({})
    const expanded = computed.flatMap((row) =>
        Array.from({ length: row.count }, (_, i) => ({ ...row, count: 1, key: `${keyOf(row)}#${i}` }))
    )
    const plan = [
        ...expanded,
        ...added.map((a) => ({
            ...a,
            map: mapIdOf(a.mapName),
            count: 1,
            kind: null as Template | null,
            enabled: false,
        })),
    ]
        .filter((row) => !removed[row.key])
        .map((row) => {
            const e = edits[row.key]
            const zone = e?.zone ?? row.zone
            const rank = e?.rank ?? row.rank
            const mapName = e?.mapName ?? row.mapName
            const feeValue = e?.fee ?? row.fee
            const kind = kindFor(mapName, feeValue, rank, zone)
            return {
                zone,
                rank,
                map: mapIdOf(mapName),
                mapName,
                fee: feeValue,
                count: 1,
                kind,
                enabled: kind ? kind.deploy_slot_regions.includes(zone) : false,
                key: row.key,
                edited: Boolean(e) || row.key.startsWith('added#'),
            }
        })
    const addLine = () => {
        const last = plan[plan.length - 1]
        setAdded((prev) => [
            ...prev,
            {
                key: `added#${Date.now()}`,
                zone: last?.zone ?? zones[0] ?? options.slot_regions[0]?.id ?? 'us',
                rank: last?.rank ?? tiers[0],
                mapName: last?.mapName ?? mainMap ?? 'MilitaryBase_v3',
                fee: last?.fee ?? '0.25',
            },
        ])
    }
    const removeLine = (key: string) => setRemoved((prev) => ({ ...prev, [key]: true }))

    /** Two ways to read the same plan. "Each" is one line per server, every
     *  one editable. "Grouped" folds identical servers into one line with a
     *  count and +/- -- shorter for a big event, and the count is what you
     *  glance at. Both are the same list underneath. */
    const [planView, setPlanView] = useState<'each' | 'grouped'>('each')
    const groupedPlan = plan.reduce(
        (out, row) => {
            const id = `${row.zone}|${row.rank}|${row.mapName}|${row.fee}`
            const hit = out.find((g) => g.id === id)
            if (hit) {
                hit.count += 1
                hit.keys.push(row.key)
            } else out.push({ id, row, count: 1, keys: [row.key] })
            return out
        },
        [] as Array<{ id: string; row: (typeof plan)[number]; count: number; keys: string[] }>
    )
    const addLike = (row: (typeof plan)[number]) =>
        setAdded((prev) => [
            ...prev,
            { key: `added#${Date.now()}`, zone: row.zone, rank: row.rank, mapName: row.mapName, fee: row.fee },
        ])
    const edit = (key: string, patch: Edit) =>
        setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] ?? {}), ...patch } }))
    const unedit = (key: string) =>
        setEdits((prev) => {
            const next = { ...prev }
            delete next[key]
            return next
        })
    const feesFor = (mapName: string) => (MAP_FEES[mapName] ?? []).map((f) => f.fee)
    /** What a line may be set to. The mix's own fees, plus free, which is
     *  never in the mix -- a free server would skew an event's economy if it
     *  were dealt out at random -- but is a fair thing to set a line to by
     *  hand. `money` already renders '0.00' as "free". */
    const feeOptions = (mapName: string) => {
        const own = feesFor(mapName).length ? feesFor(mapName) : ['0.25', '0.50', '1.00']
        return [...new Set(['0.00', ...own])].sort((a, b) => Number(a) - Number(b))
    }
    const SEL = `${INPUT} py-0 text-[11px]`

    const seatsTotal = plan.reduce((sum, row) => sum + row.count * (maxPlayersFor(row.mapName) ?? 20), 0)
    const byMapCount = mapsInMix.map(
        (m) => [m, plan.filter((r) => r.mapName === m).reduce((s, r) => s + r.count, 0)] as const
    )
    const ready = n > 0 && zones.length > 0 && mix.length > 0
    const total = plan.reduce((sum, row) => sum + row.count, 0)
    // An executive can build anything: missing kinds are made, regions switched
    // on, schedules written. Anyone else who may deploy can build NOW when every
    // line already has an enabled kind set up for its region, because then the
    // build is only deploys.
    const onlyDeploys = !schedule && plan.every((r) => r.map === null || (r.kind && r.kind.enabled && r.enabled))
    const canBuild = ready && (canEdit || (canDeploy && onlyDeploys))

    const enable = async (row: (typeof plan)[number]) => {
        if (!row.kind) return
        try {
            await saveTemplate(row.kind.id, { deploy_slot_regions: [...row.kind.deploy_slot_regions, row.zone] })
            addNotification(`${row.kind.name} can now go to ${regionLabel(row.zone)}`, 'success')
        } catch (err: any) {
            addNotification(err.message, 'error')
        }
    }

    /** Lines that are the same kind in the same region, as one deploy of N. */
    const grouped = (rows: Array<{ row: (typeof plan)[number]; kind: Template }>) => {
        const out: Array<{ row: (typeof plan)[number]; kind: Template; count: number }> = []
        for (const r of rows) {
            const hit = out.find((o) => o.kind.id === r.kind.id && o.row.zone === r.row.zone)
            if (hit) hit.count += 1
            else out.push({ ...r, count: 1 })
        }
        return out
    }

    const build = async () => {
        setBuilding(true)
        let made = 0
        try {
            // Every line gets a kind: the one that exists, or one made now.
            // A kind not yet allowed into this line's region is allowed in.
            const resolved: Array<{ row: (typeof plan)[number]; kind: Template }> = []
            const cache = new Map<string, Template>()
            for (const row of plan) {
                if (row.map === null) continue
                const key = `${row.mapName}|${row.fee}|${row.rank}`
                let kind = row.kind ?? cache.get(key) ?? null
                if (!kind) {
                    kind = await onEnsureKind(row.map, row.fee, row.rank)
                    cache.set(key, kind)
                }
                if (!kind.enabled) {
                    // Nothing was built from a switched-off kind before this
                    // board; stop before any server is made, not halfway.
                    throw new Error(`${kind.name} is switched off in Templates. Switch it on, then build.`)
                }
                if (!kind.deploy_slot_regions.includes(row.zone)) {
                    // Should not happen -- kindFor only hands back a kind
                    // allowed here, or none. If it does, a fresh kind is
                    // safer than rewriting a regional one's region list.
                    kind = await onEnsureKind(row.map, row.fee, row.rank)
                    cache.set(key, kind)
                }
                resolved.push({ row, kind })
            }

            // An event is private or it is not; there is no sense in half of
            // the regions asking for a password. Blank is left alone, because
            // an event should not silently unlock a kind that somebody made
            // private for another reason.
            const wanted = password.trim() === '' ? null : password.trim()
            if (wanted !== null) {
                const done = new Set<number>()
                for (const { kind } of resolved) {
                    if (done.has(kind.id) || (kind.password || null) === wanted) continue
                    await saveTemplate(kind.id, { password: wanted })
                    done.add(kind.id)
                }
            }

            if (schedule) {
                // One schedule per region, not one for the whole event.
                //
                // A window holds at most one row per kind: there is a unique
                // constraint on (window, template), in a non-default schema,
                // so widening it is a migration nobody wants for this. An
                // event across three regions on the same map, fee and rank is
                // that same kind three times, which is exactly what the
                // constraint refuses -- and it came back as a 500 with nothing
                // scheduled at all.
                //
                // Split by region and each window holds each kind once. It
                // also reads better on the schedules list, where US and Europe
                // are then separately switchable.
                const byRegion = new Map<string, Array<{ profile_id: number; count: number; region: string }>>()
                for (const { row, kind, count } of grouped(resolved)) {
                    const into = byRegion.get(row.zone) ?? []
                    into.push({ profile_id: kind.id, count, region: row.zone })
                    byRegion.set(row.zone, into)
                }

                if (target) {
                    const first = target.list[0]
                    const taken = new Set(windows.map((w) => w.name))
                    for (const [zone, templates] of byRegion) {
                        // The region's own schedule in that event: one whose
                        // every line builds in this region.
                        const into = target.list.find(
                            (w) => w.templates.length > 0 && w.templates.every((t) => t.region === zone)
                        )
                        if (into) {
                            const merged = into.templates.map((t) => ({
                                profile_id: t.profile_id,
                                count: t.count,
                                region: t.region,
                            }))
                            for (const t of templates) {
                                const same = merged.find((m) => m.profile_id === t.profile_id)
                                if (same) same.count += t.count
                                else merged.push(t)
                            }
                            await saveWindow(into.id, { templates: merged })
                        } else {
                            // A region the event did not have yet: its own
                            // schedule, on the event's times.
                            let name = `${first.name.split(' — ')[0]} — ${regionLabel(zone)}`.slice(0, 64)
                            for (let i = 2; taken.has(name); i++) name = `${name.slice(0, 60)} (${i})`
                            taken.add(name)
                            await saveWindow(null, {
                                name,
                                enabled: true,
                                templates,
                                deploy_at: first.deploy_at,
                                destroy_at: first.destroy_at,
                                repeat_weekly: first.repeat_weekly,
                            })
                        }
                        made += templates.reduce((sum, t) => sum + t.count, 0)
                    }
                    addNotification(
                        `${made} servers added to the schedule for ${showInZone(first.deploy_at, getZone())}`,
                        'success'
                    )
                    setAddTo(null)
                    setConfirm(false)
                    onDone()
                    return
                }

                const when = (schedule.deployAt || '').slice(0, 16)
                for (const [zone, templates] of byRegion) {
                    await saveWindow(null, {
                        // Unique and capped at 64: the region is what tells two
                        // halves of the same event apart.
                        name: `Event for ${n} — ${regionLabel(zone)} @ ${when}`.slice(0, 64),
                        enabled: true,
                        templates,
                        deploy_at: schedule.deployAt || null,
                        destroy_at: schedule.destroyAt || null,
                        repeat_weekly: schedule.repeatWeekly,
                    })
                    made += templates.reduce((sum, t) => sum + t.count, 0)
                }
                addNotification(
                    `${made} servers scheduled across ${byRegion.size} ${
                        byRegion.size === 1 ? 'region' : 'regions'
                    } for ${showInZone(schedule.deployAt, getZone())}`,
                    'success'
                )
            } else {
                for (const { row, kind, count } of grouped(resolved)) {
                    await deployTemplate(
                        kind.id,
                        count,
                        row.zone,
                        machine === 'default' ? undefined : machine,
                        joinRaces
                    )
                    made += count
                }
                addNotification(
                    `${made} servers requested for ${n} people` +
                        (joinRaces.length
                            ? `, joining ${joinRaces.length} kill race${joinRaces.length === 1 ? '' : 's'} once built`
                            : ''),
                    'success'
                )
            }
            setConfirm(false)
            onDone()
        } catch (err: any) {
            addNotification(`${err.message} (${made} of ${total} requested before it stopped)`, 'error')
        } finally {
            setBuilding(false)
        }
    }

    return (
        <>
            <Choice label='People'>
                <input
                    inputMode='numeric'
                    value={people}
                    onChange={(e) => setPeople(e.target.value)}
                    className={`${INPUT} w-20 py-0.5 text-right tabular-nums`}
                />
                <span className='text-[10px] text-white/30'>expected at the event</span>
            </Choice>
            <Choice label='Password'>
                <input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    maxLength={64}
                    placeholder='leave blank for public servers'
                    className={`${INPUT} w-60 py-0.5`}
                />
                <span className='text-[10px] text-white/30'>
                    {password.trim() === ''
                        ? 'anyone can join'
                        : 'private — every server in this event asks for it'}
                </span>
            </Choice>
            <Choice label='Regions'>
                {options.slot_regions.map((r) => (
                    <Chip
                        key={r.id}
                        on={zones.includes(r.id)}
                        onClick={() => setZones(toggle(zones, r.id))}
                        onStyle='border-sky-400/70 bg-sky-400/10 text-sky-300'
                    >
                        {r.label}
                    </Chip>
                ))}
                <span className='text-[10px] text-white/30'>people split evenly across these</span>
            </Choice>
            <Choice label='Ranks'>
                {RANKS.map((r) => (
                    <Chip
                        key={r}
                        on={ranks.includes(r)}
                        onClick={() => setRanks(toggle(ranks, r))}
                        onStyle={RANK_COPY[r].on}
                    >
                        {RANK_COPY[r].label}
                    </Chip>
                ))}
                <span className='text-[10px] text-white/30'>none picked = public only</span>
            </Choice>
            <Choice label='Loot'>
                {eventFees.length === 0 ? (
                    <span className='text-white/30'>pick a map first</span>
                ) : (
                    eventFees.map((f) => {
                        const pools = lootFor(f, rules)
                        const usdc = pools.find((p) => p.currency === 'USDC')
                        const points = pools.find((p) => p.currency === 'POINT')
                        return (
                            <span
                                key={f}
                                className='flex flex-row items-center gap-1.5 rounded border border-white/10 px-2 py-1'
                            >
                                <span className='text-violet-300'>{money(f)}</span>
                                <span className='text-white/60'>
                                    {pools.length === 0
                                        ? 'no pools'
                                        : [usdc && `USDC ${usdc.amount}`, points && `Points ${points.amount}`]
                                              .filter(Boolean)
                                              .join(' · ')}
                                </span>
                            </span>
                        )
                    })
                )}
                {canEdit && <EditLink onClick={onEditLoot} />}
                <span className='text-[10px] text-white/30'>each fee's loot, from the loot rules</span>
            </Choice>
            {schedule && openEvents.length > 0 && (
                <Choice label='Add to'>
                    <Chip
                        on={target === null}
                        onClick={() => setAddTo(null)}
                        onStyle='border-violet-400/70 bg-violet-400/10 text-violet-300'
                    >
                        New schedule
                    </Chip>
                    {openEvents.map((e) => (
                        <Chip
                            key={e.key}
                            on={target?.key === e.key}
                            onClick={() => setAddTo(e.key)}
                            onStyle='border-violet-400/70 bg-violet-400/10 text-violet-300'
                            title={e.list.map((w) => w.name).join('\n')}
                        >
                            {showInZone(e.at, getZone())}
                            {e.repeat ? ' weekly' : ''} · {e.servers} server{e.servers === 1 ? '' : 's'}
                        </Chip>
                    ))}
                    <span className='text-[10px] text-white/30'>
                        {target ? "adds these servers to it, on that schedule's times" : 'or add to one already made'}
                    </span>
                </Choice>
            )}

            {ready && (
                <div className='flex flex-col gap-1 border-t border-white/10 pt-3'>
                    <div className='flex flex-row items-baseline gap-3'>
                        <span className='text-[10px] uppercase tracking-widest text-white/50'>The plan</span>
                        <span className='text-white/40'>
                            {n} people → <span className='text-white'>{total} servers</span> (room for {seatsTotal}{' '}
                            players):{' '}
                            {byMapCount.map(([m, c], i) => (
                                <span key={m}>
                                    {i > 0 ? ', ' : ''}
                                    {c} {mapLabel(m)}
                                </span>
                            ))}
                            , dealt across {combos} {combos === 1 ? 'group' : 'groups'}
                        </span>
                        <span className='ml-auto flex flex-row overflow-hidden rounded border border-white/20'>
                            {(
                                [
                                    ['each', 'every server'],
                                    ['grouped', 'grouped'],
                                ] as const
                            ).map(([key, label]) => (
                                <button
                                    key={key}
                                    type='button'
                                    onClick={() => setPlanView(key)}
                                    className={`px-2.5 py-0.5 text-[10px] uppercase tracking-widest ${
                                        planView === key ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </span>
                    </div>
                    {planView === 'grouped' &&
                        groupedPlan.map((g) => (
                            <div
                                key={g.id}
                                className='grid grid-cols-[8rem,5.5rem,8rem,4.5rem,6rem,5.5rem,minmax(0,1fr)] items-center gap-2 rounded border border-white/10 px-3 py-1.5'
                            >
                                <span className='text-sky-300'>{regionLabel(g.row.zone)}</span>
                                <span className={RANK_COPY[g.row.rank].style}>{RANK_COPY[g.row.rank].label}</span>
                                <span className='truncate text-white/70'>{mapLabel(g.row.mapName)}</span>
                                <span className='text-right tabular-nums text-violet-300'>{money(g.row.fee)}</span>
                                <span className='flex flex-row items-center gap-1.5'>
                                    <button
                                        type='button'
                                        onClick={() => removeLine(g.keys[g.keys.length - 1])}
                                        className='rounded border border-white/15 px-1.5 text-white/50 hover:border-white/40 hover:text-white'
                                        title='One fewer'
                                    >
                                        −
                                    </button>
                                    <span className='w-8 text-center tabular-nums'>× {g.count}</span>
                                    <button
                                        type='button'
                                        onClick={() => addLike(g.row)}
                                        className='rounded border border-white/15 px-1.5 text-white/50 hover:border-white/40 hover:text-white'
                                        title='One more'
                                    >
                                        +
                                    </button>
                                </span>
                                <span className='tabular-nums text-white/40'>
                                    {g.count * (maxPlayersFor(g.row.mapName) ?? 20)} players
                                    <span className='text-white/25'> · {maxPlayersFor(g.row.mapName) ?? 20} each</span>
                                </span>
                                <span className='truncate text-white/40'>
                                    {g.row.kind ? `${g.row.kind.running_servers} running now` : 'first of its kind'}
                                </span>
                            </div>
                        ))}
                    {planView === 'each' &&
                        plan.map((row, i) => (
                            <div
                                key={row.key}
                                className={`grid grid-cols-[2rem,8rem,5.5rem,8rem,4.5rem,5.5rem,minmax(0,1fr),4rem] items-center gap-2 rounded border px-3 py-1.5 ${
                                    row.edited ? 'border-violet-400/40' : 'border-white/10'
                                }`}
                            >
                                <span className='tabular-nums text-white/30'>#{i + 1}</span>
                                <select
                                    value={row.zone}
                                    onChange={(e) => edit(row.key, { zone: e.target.value })}
                                    className={`${SEL} text-sky-300`}
                                >
                                    {options.slot_regions.map((r) => (
                                        <option key={r.id} value={r.id} className='bg-neutral-900'>
                                            {r.label}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={row.rank}
                                    onChange={(e) => edit(row.key, { rank: e.target.value as Rank })}
                                    className={`${SEL} ${RANK_COPY[row.rank].style}`}
                                >
                                    {RANKS.map((r) => (
                                        <option key={r} value={r} className='bg-neutral-900'>
                                            {RANK_COPY[r].label}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={row.mapName}
                                    onChange={(e) => {
                                        const next = e.target.value
                                        // Keeps a hand-set fee across a map
                                        // change when the new map offers it,
                                        // free included. Falls back to the
                                        // map's cheapest paid fee rather than
                                        // to free, which nobody asked for.
                                        const fees = feeOptions(next)
                                        edit(row.key, {
                                            mapName: next,
                                            fee: fees.includes(row.fee)
                                                ? row.fee
                                                : fees.find((f) => Number(f) > 0) ?? fees[0],
                                        })
                                    }}
                                    className={`${SEL} text-white/70`}
                                >
                                    {maps.map((m) => (
                                        <option key={m.id} value={m.name} className='bg-neutral-900'>
                                            {mapLabel(m.name)}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={row.fee}
                                    onChange={(e) => edit(row.key, { fee: e.target.value })}
                                    className={`${SEL} text-violet-300`}
                                >
                                    {feeOptions(row.mapName).map((f) => (
                                        <option key={f} value={f} className='bg-neutral-900'>
                                            {money(f)}
                                        </option>
                                    ))}
                                </select>
                                <span className='tabular-nums text-white/40'>
                                    {maxPlayersFor(row.mapName) ?? 20} players
                                </span>
                                <span className='flex min-w-0 flex-row items-center gap-2 text-white/40'>
                                    <span className='truncate'>
                                        {row.kind ? `${row.kind.running_servers} running now` : 'first of its kind'}
                                    </span>
                                    {row.edited && !row.key.startsWith('added#') && (
                                        <button
                                            type='button'
                                            onClick={() => unedit(row.key)}
                                            className='shrink-0 text-violet-300 underline'
                                        >
                                            reset
                                        </button>
                                    )}
                                </span>
                                <button
                                    type='button'
                                    onClick={() => removeLine(row.key)}
                                    title='Drop this server from the plan'
                                    className='text-right text-[10px] uppercase tracking-widest text-white/30 hover:text-red-400'
                                >
                                    remove
                                </button>
                            </div>
                        ))}
                    <button
                        type='button'
                        onClick={addLine}
                        className='self-start rounded border border-dashed border-white/20 px-3 py-1 text-[10px] uppercase tracking-widest text-white/40 hover:border-white/40 hover:text-white'
                    >
                        + add a server
                    </button>
                    <div className='flex flex-row flex-wrap items-end gap-3 pt-1'>
                        <label className='flex flex-col gap-1'>
                            <span className='text-[10px] uppercase tracking-widest text-white/50'>Machine</span>
                            <select value={machine} onChange={(e) => setMachine(e.target.value)} className={INPUT}>
                                {MACHINE_TYPES.map((m) => (
                                    <option key={m.value} value={m.value} className='bg-neutral-900'>
                                        {m.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <ActionButton
                            onClick={async () => setConfirm(true)}
                            disabled={!canBuild || building || (schedule !== null && !target && !schedule.deployAt)}
                            className='border-violet-500 bg-violet-600 px-5 py-1.5 font-bold text-white enabled:hover:bg-violet-500'
                        >
                            <FontAwesomeIcon icon={faRocket} className='mr-1' />{' '}
                            {target
                                ? `Add ${total} to the schedule`
                                : schedule
                                ? `Schedule all ${total}`
                                : `Make all ${total}`}
                        </ActionButton>
                    </div>
                    {confirm && (
                        <ConfirmBar
                            message={`Build ${total} servers for ${n} people?`}
                            detail={`${plan.map((r) => `${r.count} ${RANK_COPY[r.rank].label.toLowerCase()} in ${regionLabel(r.zone)}`).join(', ')}. Each is a real machine on GCP.`}
                            confirmLabel={`Build ${total}`}
                            busy={building}
                            onCancel={() => setConfirm(false)}
                            onConfirm={build}
                        />
                    )}
                </div>
            )}
        </>
    )
}

const Choice = ({ label, children }: { label: string; children: any }) => (
    <div className='flex flex-row flex-wrap items-center gap-1.5'>
        <span className='w-20 shrink-0 text-[10px] uppercase tracking-widest text-white/50'>{label}</span>
        {children}
    </div>
)

const Chip = ({
    on,
    onClick,
    onStyle,
    title,
    children,
}: {
    on: boolean
    onClick: () => void
    onStyle: string
    title?: string
    children: any
}) => (
    <button
        type='button'
        onClick={onClick}
        title={title}
        className={`rounded border px-2.5 py-1 text-[10px] uppercase tracking-widest transition-colors ${on ? onStyle : CHIP_OFF}`}
    >
        {children}
    </button>
)

/** One pool on the loot row: the balance (typed or derived) and the reward
 *  range that follows from it. */
const PoolBox = ({
    label,
    balance,
    onBalance,
    pool,
    auto,
}: {
    label: string
    balance: string
    onBalance: (v: string) => void
    pool?: LootPool
    auto: boolean
}) => (
    <span className='flex flex-row items-center gap-1.5 rounded border border-white/10 px-2 py-1'>
        <span className='text-[10px] uppercase tracking-widest text-white/50'>{label}</span>
        <input
            inputMode='decimal'
            value={balance}
            onChange={(e) => onBalance(e.target.value)}
            disabled={auto}
            className={`${INPUT} w-20 py-0.5 text-right tabular-nums ${auto ? 'border-transparent' : ''}`}
        />
        <span className='text-[10px] text-white/30'>
            {pool ? `pays ${pool.reward_min}–${pool.reward_max}` : 'no pool'}
        </span>
    </span>
)

/** Ranks or loot, on two tabs: the rule every server made from now on
 *  follows, and the one kind of server currently picked above. Changing the
 *  rule moves nothing that already exists; changing the kind moves only it. */
const RulesSheet = ({
    section,
    rules,
    rulesMeta,
    current,
    currentLoot,
    canEdit,
    onSave,
    onSavedKind,
    onClose,
}: {
    section: 'rank' | 'loot'
    rules: Rules
    rulesMeta: { updatedAt: string | null; updatedBy: string | null }
    /** The kind of server picked above, if the three choices match one. */
    current: Template | null
    /** The loot row as shown above, for "use these on this kind". */
    currentLoot: LootPool[]
    canEdit: boolean
    onSave: (r: Rules) => void
    onSavedKind: () => void
    onClose: () => void
}) => {
    const { addNotification } = useNotification()
    const [tab, setTab] = useState<'forward' | 'kind'>('forward')
    const [draft, setDraft] = useState<Record<keyof Rules, string>>(
        Object.fromEntries(Object.entries(rules).map(([k, v]) => [k, String(v)])) as Record<keyof Rules, string>
    )
    // This kind only.
    const [minS, setMinS] = useState(current?.min_sessions == null ? '' : String(current.min_sessions))
    const [maxS, setMaxS] = useState(current?.max_sessions == null ? '' : String(current.max_sessions))
    const [pools, setPools] = useState<LootPool[]>(current?.loot_pools ?? [])
    const [saving, setSaving] = useState(false)

    const num = (k: keyof Rules) => Number(draft[k]) || 0
    const set = (k: keyof Rules) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setDraft((d) => ({ ...d, [k]: e.target.value }))

    const example = lootFor('0.25', {
        ...rules,
        usdcPerFee: num('usdcPerFee'),
        usdcMinDiv: num('usdcMinDiv') || 1,
        usdcMaxDiv: num('usdcMaxDiv') || 1,
        pointPerFee: num('pointPerFee'),
        pointMinDiv: num('pointMinDiv') || 1,
        pointMaxDiv: num('pointMaxDiv') || 1,
    })

    const row = (k: keyof Rules, label: string, hint: string) => (
        <label key={k} className='grid grid-cols-[10rem,6rem,1fr] items-center gap-3'>
            <span className='text-[10px] uppercase tracking-widest text-white/60'>{label}</span>
            <input
                inputMode='decimal'
                value={draft[k]}
                onChange={set(k)}
                className={`${INPUT} py-0.5 text-right tabular-nums`}
            />
            <span className='text-white/30'>{hint}</span>
        </label>
    )

    const setPool = (currency: string, patch: Partial<LootPool>) =>
        setPools((prev) => {
            const i = prev.findIndex((p) => p.currency === currency)
            if (i === -1)
                return [
                    ...prev,
                    { currency, amount: '0', reward_min: '0', reward_max: '0', auto_add_from_spawn_fee: '0', ...patch },
                ]
            const next = [...prev]
            next[i] = { ...next[i], ...patch }
            return next
        })

    const poolRow = (currency: string, label: string) => {
        const pool = pools.find((p) => p.currency === currency)
        const field = (k: keyof LootPool, l: string) => (
            <label className='flex flex-col gap-0.5'>
                <span className='text-[10px] uppercase tracking-widest text-white/40'>{l}</span>
                <input
                    inputMode='decimal'
                    value={pool?.[k] ?? ''}
                    onChange={(e) => setPool(currency, { [k]: e.target.value })}
                    disabled={!canEdit}
                    className={`${INPUT} w-24 py-0.5 text-right tabular-nums`}
                />
            </label>
        )
        return (
            <div key={currency} className='flex flex-row flex-wrap items-end gap-3'>
                <span className='w-14 text-[10px] uppercase tracking-widest text-white/60'>{label}</span>
                {field('amount', 'balance')}
                {field('reward_min', 'reward min')}
                {field('reward_max', 'reward max')}
                {/* Only the USDC pool is ever topped up, from the USDC actually
                    paid (D-020). A POINT top-up did nothing, so it is not offered. */}
                {currency === 'USDC' && field('auto_add_from_spawn_fee', 'top-up share')}
            </div>
        )
    }

    const saveKind = async () => {
        if (!current) return
        setSaving(true)
        try {
            if (section === 'rank') {
                const on = minS !== '' || maxS !== ''
                await saveTemplate(current.id, {
                    check_requirements: on,
                    min_sessions: minS === '' ? null : Number(minS),
                    max_sessions: maxS === '' ? null : Number(maxS),
                })
            } else {
                await saveTemplate(current.id, { loot_pools: pools.filter((p) => Number(p.amount) > 0) })
            }
            addNotification(`Saved ${current.name}`, 'success')
            onSavedKind()
            onClose()
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setSaving(false)
        }
    }

    const title = section === 'rank' ? 'Ranks' : 'Loot'

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className='flex w-full max-w-xl flex-col gap-4 rounded border border-white/20 bg-zinc-900 p-4 text-xs font-mono text-white'
            >
                <div className='flex flex-row items-baseline justify-between'>
                    <span className='text-sm font-bold'>{title}</span>
                    <button type='button' onClick={onClose} className='text-white/40 underline hover:text-white'>
                        close
                    </button>
                </div>

                <nav className='flex flex-row gap-1 border-b border-white/10'>
                    {(
                        [
                            ['forward', 'Servers from now on'],
                            ['kind', current ? `This kind only` : 'This kind only'],
                        ] as const
                    ).map(([key, label]) => (
                        <button
                            key={key}
                            type='button'
                            onClick={() => setTab(key)}
                            className={`-mb-px border-b-2 px-3 py-1.5 text-[10px] uppercase tracking-widest ${
                                tab === key
                                    ? 'border-violet-500 text-white'
                                    : 'border-transparent text-white/50 hover:text-white/80'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </nav>

                {tab === 'forward' && section === 'rank' && (
                    <div className='flex flex-col gap-2'>
                        {row('noobMax', 'Noob: up to', 'spawns. A player with more is turned away.')}
                        {row('proMin', 'Pro: at least', 'spawns. A player with fewer is turned away.')}
                        <span className='text-[10px] text-white/30'>
                            Changes what Noob and Pro mean for every server made from here on. Existing kinds keep their
                            limits until edited on the other tab.
                        </span>
                    </div>
                )}

                {tab === 'forward' && section === 'loot' && (
                    <div className='flex flex-col gap-2'>
                        {row('usdcPerFee', 'USDC balance', '× the spawn fee')}
                        {row('usdcMinDiv', 'USDC reward min', '= balance ÷ this')}
                        {row('usdcMaxDiv', 'USDC reward max', '= balance ÷ this')}
                        {row('pointPerFee', 'Points balance', '× the spawn fee')}
                        {row('pointMinDiv', 'Points reward min', '= balance ÷ this')}
                        {row('pointMaxDiv', 'Points reward max', '= balance ÷ this')}
                        <div className='rounded border border-white/10 p-2 text-white/60'>
                            <span className='text-[10px] uppercase tracking-widest text-white/40'>
                                So a $0.25 server gets
                            </span>
                            {example.map((p) => (
                                <span key={p.currency} className='ml-2'>
                                    {p.currency} {p.amount}, {lootDrawLabel(p.currency, p.reward_min, p.reward_max)}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {tab === 'forward' && (
                    <div className='flex flex-row items-center justify-between'>
                        <span className='text-[10px] text-white/30'>
                            One set for every staff member.{' '}
                            {rulesMeta.updatedAt
                                ? `Last changed${rulesMeta.updatedBy ? ` by ${rulesMeta.updatedBy}` : ''} ${showInZone(
                                      rulesMeta.updatedAt,
                                      getZone()
                                  )}.`
                                : 'Never changed: these are the defaults.'}
                            {!canEdit && ' Only an executive can change them.'}
                        </span>
                        <button
                            type='button'
                            disabled={!canEdit}
                            onClick={() => {
                                onSave(
                                    Object.fromEntries(
                                        Object.entries(draft).map(([k, v]) => [k, Number(v) || 0])
                                    ) as Rules
                                )
                                onClose()
                            }}
                            className='rounded bg-violet-600/80 px-4 py-1 text-xs font-bold uppercase tracking-wide hover:bg-violet-600 disabled:cursor-not-allowed disabled:opacity-40'
                        >
                            Save rule
                        </button>
                    </div>
                )}

                {tab === 'kind' && !current && (
                    <span className='text-white/40'>
                        Pick a map, a fee and a rank above that already exist, then come back here to change just that
                        kind.
                    </span>
                )}

                {tab === 'kind' && current && section === 'rank' && (
                    <div className='flex flex-col gap-3'>
                        <span className='text-white/60'>
                            <span className='font-bold text-white'>{current.name}</span> — its own limits, whatever the
                            rule says.
                        </span>
                        <div className='flex flex-row flex-wrap gap-3'>
                            <label className='flex flex-col gap-0.5'>
                                <span className='text-[10px] uppercase tracking-widest text-white/40'>
                                    at least (spawns)
                                </span>
                                <input
                                    inputMode='numeric'
                                    value={minS}
                                    onChange={(e) => setMinS(e.target.value)}
                                    disabled={!canEdit}
                                    placeholder='any'
                                    className={`${INPUT} w-28 py-0.5 text-right tabular-nums`}
                                />
                            </label>
                            <label className='flex flex-col gap-0.5'>
                                <span className='text-[10px] uppercase tracking-widest text-white/40'>
                                    at most (spawns)
                                </span>
                                <input
                                    inputMode='numeric'
                                    value={maxS}
                                    onChange={(e) => setMaxS(e.target.value)}
                                    disabled={!canEdit}
                                    placeholder='any'
                                    className={`${INPUT} w-28 py-0.5 text-right tabular-nums`}
                                />
                            </label>
                        </div>
                        <span className='text-[10px] text-white/30'>
                            Both blank means public. Limits that are not exactly the rule show as "custom" above.
                        </span>
                    </div>
                )}

                {tab === 'kind' && current && section === 'loot' && (
                    <div className='flex flex-col gap-3'>
                        <span className='flex flex-row flex-wrap items-baseline gap-2 text-white/60'>
                            <span className='font-bold text-white'>{current.name}</span> — its own pools, whatever the
                            rule says.
                            {canEdit && (
                                <button
                                    type='button'
                                    onClick={() => setPools(currentLoot)}
                                    className='text-violet-300 underline'
                                >
                                    use the loot row above
                                </button>
                            )}
                        </span>
                        {poolRow('USDC', 'USDC')}
                        {poolRow('POINT', 'Points')}
                        <span className='text-[10px] text-white/30'>
                            A pool with a zero balance is removed on save.
                        </span>
                    </div>
                )}

                {tab === 'kind' && current && canEdit && (
                    <div className='flex flex-row justify-end'>
                        <button
                            type='button'
                            onClick={saveKind}
                            disabled={saving}
                            className='rounded bg-violet-600/80 px-4 py-1 text-xs font-bold uppercase tracking-wide hover:bg-violet-600 disabled:opacity-30'
                        >
                            {saving ? 'saving…' : `Save ${current.name}`}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

const TemplateSheet = ({
    template,
    options,
    canEdit,
    rules,
    onClose,
    onSaved,
}: {
    template: Template
    options: Options
    canEdit: boolean
    rules: Rules
    onClose: () => void
    onSaved: () => void
}) => {
    const { addNotification } = useNotification()
    const original = useMemo(() => formFrom(template), [template])
    const [form, setForm] = useState<TemplateFormState>(original)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [saving, setSaving] = useState(false)
    const dirty = JSON.stringify(form) !== JSON.stringify(original)
    const r = rankOf(template, rules)

    const save = async () => {
        setSaving(true)
        setErrors({})
        try {
            await saveTemplate(template.id, payloadOf(form))
            addNotification(`Saved ${template.name}`, 'success')
            onSaved()
        } catch (err: any) {
            setErrors(parseFieldErrors(err.message))
            addNotification(err.message, 'error')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className='flex max-h-[88vh] w-full max-w-3xl flex-col gap-3 overflow-auto rounded border border-white/20 bg-zinc-900 p-4 text-xs font-mono text-white'
            >
                <div className='flex flex-row items-baseline justify-between gap-3'>
                    <span className='flex min-w-0 flex-row items-baseline gap-2'>
                        <span className='truncate text-sm font-bold'>{template.name}</span>
                        <span className={`uppercase tracking-wide ${RANK_COPY[r].style}`}>{RANK_COPY[r].label}</span>
                        <span className='text-white/40'>
                            {mapLabel(template.map_name)} · {money(template.fee)} · running on{' '}
                            {template.running_servers}
                        </span>
                    </span>
                    <button
                        type='button'
                        onClick={onClose}
                        className='shrink-0 text-white/40 underline hover:text-white'
                    >
                        close
                    </button>
                </div>
                <TemplateFormFields
                    form={form}
                    setForm={setForm}
                    options={options}
                    errors={errors}
                    disabled={!canEdit}
                />
                {canEdit && (
                    <div className='flex flex-row items-center justify-end gap-3'>
                        {dirty && <span className='uppercase tracking-wide text-white/50'>unsaved changes</span>}
                        <button
                            type='button'
                            onClick={save}
                            disabled={!dirty || saving}
                            className='rounded bg-violet-600/80 px-4 py-1 text-xs font-bold uppercase tracking-wide hover:bg-violet-600 disabled:opacity-30'
                        >
                            {saving ? 'saving…' : 'Save'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
